"use client";

import { useContext, useEffect, useState } from "react";
import { QuizOption } from "./components/quiz-option";
import { logEvent } from "@/service/logs/log";
import { ResourceContext } from "@/contexts/resource-context";
import { AssessmentScoreContext } from "@/contexts/assessment-score-context";
import { ExpandableImage } from "@/components/common/expandable-image";
import striptags from "striptags";
import { motion } from "motion/react";

import {
  areAllCorrectSelected,
  getIsCompleted,
  getOptionByIndexFromQuiz,
  getSelectedFromScore,
  hasCommentContent,
} from "./quiz-service";
import { createMessageGenerator } from "@/components/common/quiz/quiz-messages";
import { QuizFeedback } from "./quiz-feedback";
import { QuizCalloutBlock } from "./components/quiz-callout-block";
import { clientPost } from "@/service/client-api/client-api";
import { scrollToPos } from "@/service/scroll/scroll";

export const Quiz = ({
  quiz,
  setCanAdvance,
  completedEvent,
  stepIdx,
  showHeading = true,
  resourceId,
  readonly = false,
}: {
  quiz: QuizDetails;
  setCanAdvance: (canAdvance: boolean) => void;
  completedEvent?:
    | "clinical_case_step"
    | "course_quiz_step"
    | "roadmap_step_quiz";
  stepIdx?: number;
  showHeading?: boolean;
  onAnswer?: (answer: boolean) => void;
  resourceId?: number;
  readonly?: boolean;
}) => {
  const { score, setScore } = useContext(AssessmentScoreContext);
  const { logView } = useContext(ResourceContext);

  // Create isolated message generator for this quiz instance
  const messageGenerator = createMessageGenerator();

  const [selected, setSelected] = useState<number[]>(
    getSelectedFromScore(score, quiz),
  );

  const [isCompleted, setIsCompleted] = useState(
    getIsCompleted(selected, quiz.correct),
  );

  const [multipleChoiceMessage, setMultipleChoiceMessage] = useState(
    messageGenerator.getRandomMultipleChoiceMessage(),
  );

  const [finalAnswerMessage, setFinalAnswerMessage] = useState<string | null>(
    null,
  );

  useEffect(() => {
    setMultipleChoiceMessage(messageGenerator.getRandomMultipleChoiceMessage());
  }, [selected.length]);

  const [manualComment, setManualComment] = useState<ContentBlock[]>();
  const [activeCommentIdx, setActiveCommentIdx] = useState<number | null>(null);

  useEffect(() => {
    if (readonly) return;

    if (isCompleted && completedEvent === "course_quiz_step") {
      scrollToPos({ top: Math.max(0, 112), behavior: "smooth" });
    }

    if (isCompleted && completedEvent) {
      logEvent(completedEvent, logView, {
        type: quiz.correct.length > 1 ? "multiselect" : "select",
        step: stepIdx,
        correctAnswer: areAllCorrectSelected(selected, quiz.correct),
        answer: selected,
      });

      if (completedEvent === "roadmap_step_quiz") {
        clientPost(
          "roadmap.step.quiz.answer",
          { id: resourceId as number },
          {
            questionUuid: quiz.uuid,
            answerUuids: selected.map((idx) => quiz.options[idx].uuid),
            questionText: striptags(quiz.heading, ["span"]) ?? "",
            answerText: selected
              .map((idx) => striptags(quiz.options[idx].description, ["span"]))
              .join(", "),
            correctAnswerUuids: quiz.correct.map(
              (idx) => quiz.options[idx].uuid,
            ),
            isCorrect: areAllCorrectSelected(selected, quiz.correct),
          },
        );
      }
    }

    setCanAdvance(isCompleted);
  }, [isCompleted, setCanAdvance]);

  useEffect(() => {
    setIsCompleted(getIsCompleted(selected, quiz.correct));
    setScore((draft) => {
      draft[quiz.uuid] = {
        correct: areAllCorrectSelected(selected, quiz.correct),
        selected: selected,
      };
    });

    // Set final answer message when quiz is completed
    if (getIsCompleted(selected, quiz.correct)) {
      const isCorrect = areAllCorrectSelected(selected, quiz.correct);
      setFinalAnswerMessage(
        isCorrect
          ? messageGenerator.getRandomCorrectAnswerMessage()
          : messageGenerator.getRandomIncorrectAnswerMessage(),
      );
    }
  }, [selected]);

  function handleOptionSelect(optionIdx: number) {
    if (isCompleted) return;
    setSelected((prev) => {
      if (prev.includes(optionIdx)) return prev;
      return [...prev, optionIdx];
    });
  }

  const selectedOptionComment = getOptionByIndexFromQuiz(
    quiz,
    selected[selected.length - 1],
  )?.comment;

  // Jedyne omówienie w pytaniu chowało się za ikoną przy odpowiedzi, której użytkownik często
  // nawet nie zaznaczył — po wyczyszczeniu komentarzy sprowadzonych do samego werdyktu zdarza się
  // to nagminnie. Gdy quiz nie ma własnego komentarza, a treść ma dokładnie jedna odpowiedź,
  // pokazujemy ją pod pytaniem jako domyślny komentarz i chowamy jej ikonę, żeby nie dublować.
  const commentedOptions = quiz.options.filter((option) =>
    hasCommentContent(option.comment),
  );
  const promotedOption =
    !hasCommentContent(quiz.comment) && commentedOptions.length === 1
      ? commentedOptions[0]
      : undefined;

  return (
    <div>
      {showHeading && striptags(quiz.heading, ["span"]).length > 0 && (
        <h2
          className="mb-6 text-lg font-semibold md:max-w-[80%]"
          dangerouslySetInnerHTML={{ __html: quiz.heading }}
        ></h2>
      )}
      {quiz.image && (
        <ExpandableImage
          pictureProps={{
            image: quiz.image,
            className: "mx-4",
            aspectRatio: "16/9",
          }}
        />
      )}

      <motion.ul
        className="mt-5 mb-10 flex flex-col gap-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {quiz.options.map((option, idx) => (
          <QuizOption
            key={idx}
            option={option}
            onSelect={() => handleOptionSelect(idx)}
            isSelected={selected.includes(idx)}
            isAfterSelect={isCompleted}
            onShowComment={() => {
              setManualComment(option.comment);
              setActiveCommentIdx(idx);
            }}
            isCommentActive={activeCommentIdx === idx}
            showCommentIcon={
              hasCommentContent(option.comment) && option !== promotedOption
            }
            quizHasMultipleCorrect={quiz.correct.length > 1}
          />
        ))}
      </motion.ul>

      <QuizFeedback
        selected={selected}
        isCompleted={isCompleted}
        multipleChoiceMessage={multipleChoiceMessage}
        finalAnswerMessage={finalAnswerMessage}
        selectedOptionComment={
          selectedOptionComment && selectedOptionComment.length > 0
            ? selectedOptionComment
            : []
        }
        quizComment={promotedOption?.comment ?? quiz.comment ?? []}
        correct={quiz.correct}
      />

      {manualComment && (
        <QuizCalloutBlock
          contentBlocks={manualComment}
          title="Komentarz"
          variant="comment"
        />
      )}
    </div>
  );
};
